import type { PointerEvent as ReactPointerEvent } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import type { LatticeHandle } from './createLattice'
import styles from './NetworkLattice.module.css'

/**
 * The lattice, mounted only while it is on screen.
 *
 * Three.js is pulled in with a dynamic import inside the observer callback,
 * so a visitor who never reaches this section never downloads it. The loop
 * is paused whenever the section leaves the viewport, and under reduced
 * motion a single static frame is drawn instead.
 */
export const NetworkLattice = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const handleRef = useRef<LatticeHandle | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // WebGL on a low-power phone is not worth the battery for a backdrop.
    const coarse = window.matchMedia('(pointer: coarse)').matches
    if (coarse) return

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let cancelled = false

    const accent = getComputedStyle(document.documentElement)
      .getPropertyValue('--palette-accent')
      .trim()
      .split(/\s+/)
      .map(Number) as [number, number, number]

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        if (entry.isIntersecting && !handleRef.current) {
          void import('./createLattice').then(({ createLattice }) =>
            createLattice({ canvas, accent, still }).then((handle) => {
              if (cancelled) {
                handle.dispose()
                return
              }
              handleRef.current = handle
            }),
          )
        }
        handleRef.current?.setRunning(entry.isIntersecting)
      },
      { rootMargin: '150px' },
    )
    observer.observe(canvas)

    return () => {
      cancelled = true
      observer.disconnect()
      handleRef.current?.dispose()
      handleRef.current = null
    }
  }, [])

  const handlePointer = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    handleRef.current?.setPointer(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      ((event.clientY - rect.top) / rect.height) * 2 - 1,
    )
  }, [])

  return (
    <div className={styles.wrap} onPointerMove={handlePointer} aria-hidden="true">
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  )
}
